import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ShapeCanvas from './components/ShapeCanvas'
import ShapeSelector from './components/ShapeSelector'
import {
  getShapePath,
  isValidShapeId,
  SHAPE_IDS,
  deriveShapesForRound,
} from './utils/shapes'
import {
  applySimultaneousProgress,
  isComplete as checkIsComplete,
} from './utils/tracing'
import './App.css'

const SIDES = ['left', 'right']

function initialSideState() {
  return { isActive: false, userPath: [], cleared: 0, lastSample: null }
}

function randomShapeId(rng = Math.random) {
  return SHAPE_IDS[Math.floor(rng() * SHAPE_IDS.length)]
}

// When the activity is wired to a Rails ActivitySession we derive both shapes
// deterministically from (sessionId, counter). When run standalone the kid
// picks shapes manually with the small selector buttons.
function pickInitialShapes({ sessionId, initialCounter }) {
  if (sessionId != null) {
    return deriveShapesForRound(sessionId, initialCounter)
  }
  return { left: 'square', right: 'circle' }
}

export default function App({
  // Standalone-only:
  randomFn,
  // Rails-wired props:
  sessionId,
  activityId,
  requiredPairs = 10,
  initialCounter = 0,
  onProgressSave,
  onComplete,
  railsEnv,
  // Optional UI strings (Rails passes Vietnamese labels via translations).
  labels = {},
} = {}) {
  const railsMode = sessionId != null
  const [counter, setCounter] = useState(initialCounter)
  const [shapes, setShapes] = useState(() =>
    pickInitialShapes({ sessionId, initialCounter })
  )
  const [sides, setSides] = useState({
    left: initialSideState(),
    right: initialSideState(),
  })
  // True after the current pair is completed but before the kid presses the
  // continue/new button. The completed traces stay on-screen until then.
  const [pairComplete, setPairComplete] = useState(false)
  // True once the kid has cleared every required pair and the activity is
  // fully finished (Rails mode only).
  const [activityFinished, setActivityFinished] = useState(false)
  const onCompleteFiredRef = useRef(false)

  const paths = useMemo(
    () => ({ left: getShapePath(shapes.left), right: getShapePath(shapes.right) }),
    [shapes]
  )

  const continueLabel = labels.continue || 'Tiếp tục'
  const newLabel = labels.new || 'New'
  const pairCounterLabel = labels.pairCounter || ((c, t) => `${c}/${t} pairs`)

  const handleSelectShape = useCallback((side, shapeId) => {
    if (!isValidShapeId(shapeId)) return
    if (railsMode) return // selectors are hidden in Rails mode; defensive guard
    setShapes((prev) => ({ ...prev, [side]: shapeId }))
    setSides({ left: initialSideState(), right: initialSideState() })
    setPairComplete(false)
  }, [railsMode])

  const handleStandaloneNew = useCallback(() => {
    setShapes({ left: randomShapeId(randomFn), right: randomShapeId(randomFn) })
    setSides({ left: initialSideState(), right: initialSideState() })
    setPairComplete(false)
  }, [randomFn])

  const handleContinue = useCallback(() => {
    setShapes(deriveShapesForRound(sessionId, counter))
    setSides({ left: initialSideState(), right: initialSideState() })
    setPairComplete(false)
  }, [sessionId, counter])

  // Test helper — skips the trace ceremony and pretends the kid just
  // completed the current pair. Wired to a button in test/development mode
  // so cucumber can drive completion without canvas gymnastics.
  const fakeCompletePair = useCallback(() => {
    if (!railsMode || pairComplete) return
    const nextCounter = counter + 1
    setCounter(nextCounter)
    setPairComplete(true)
    if (onProgressSave) onProgressSave({ completed_pairs: nextCounter })
    if (nextCounter >= requiredPairs) setActivityFinished(true)
  }, [railsMode, pairComplete, counter, onProgressSave, requiredPairs])

  const handlePointerDown = useCallback((side, point) => {
    setSides((prev) => ({
      ...prev,
      [side]: {
        ...prev[side],
        isActive: true,
        userPath: [point],
        lastSample: point,
      },
    }))
  }, [])

  const handlePointerMove = useCallback((side, point) => {
    setSides((prev) => {
      const current = prev[side]
      if (!current.isActive) return prev
      const otherSide = side === 'left' ? 'right' : 'left'
      const other = prev[otherSide]
      const updatedUserPath = [...current.userPath, point]

      const progress = applySimultaneousProgress({
        leftPath: paths.left,
        rightPath: paths.right,
        leftCleared: prev.left.cleared,
        rightCleared: prev.right.cleared,
        leftSample: side === 'left' ? point : other.lastSample,
        rightSample: side === 'right' ? point : other.lastSample,
        leftPrevSample: prev.left.lastSample,
        rightPrevSample: prev.right.lastSample,
        leftActive: side === 'left' ? true : prev.left.isActive,
        rightActive: side === 'right' ? true : prev.right.isActive,
      })

      return {
        ...prev,
        [side]: {
          ...current,
          userPath: updatedUserPath,
          lastSample: point,
          cleared: side === 'left' ? progress.left : progress.right,
        },
        [otherSide]: {
          ...other,
          cleared: otherSide === 'left' ? progress.left : progress.right,
        },
      }
    })
  }, [paths])

  const handlePointerUp = useCallback((side) => {
    setSides((prev) => {
      const alreadyComplete = checkIsComplete(
        paths.left,
        paths.right,
        prev.left.cleared,
        prev.right.cleared
      )
      if (alreadyComplete) {
        return {
          ...prev,
          [side]: { ...prev[side], isActive: false },
        }
      }
      // Either finger lifted before both shapes finished — reset both sides.
      return { left: initialSideState(), right: initialSideState() }
    })
  }, [paths])

  const isPairFullyTraced = checkIsComplete(
    paths.left,
    paths.right,
    sides.left.cleared,
    sides.right.cleared
  )

  // When a pair is completed for the first time, bump the counter, persist
  // progress to Rails, and either finish the activity or wait for "Tiếp tục".
  useEffect(() => {
    if (!isPairFullyTraced || pairComplete) return
    setPairComplete(true)
    const nextCounter = counter + 1
    setCounter(nextCounter)
    if (railsMode && onProgressSave) {
      onProgressSave({ completed_pairs: nextCounter })
    }
    if (railsMode && nextCounter >= requiredPairs) {
      setActivityFinished(true)
    }
  }, [isPairFullyTraced, pairComplete, counter, railsMode, onProgressSave, requiredPairs])

  // Fire onComplete exactly once when the kid has finished every required pair.
  useEffect(() => {
    if (!activityFinished || onCompleteFiredRef.current) return
    onCompleteFiredRef.current = true
    if (onComplete) {
      onComplete({ completed: true, completed_pairs: counter })
    }
  }, [activityFinished, counter, onComplete])

  const bothActive = sides.left.isActive && sides.right.isActive

  const statusText = (() => {
    if (activityFinished) return labels.activityFinished || 'All done! Great job.'
    if (pairComplete) return labels.pairComplete || 'Pair complete!'
    if (bothActive) return labels.tracing || 'Tracing… keep going!'
    return labels.idle || 'Place a finger on each shape and start tracing.'
  })()

  const showContinue = railsMode && pairComplete && !activityFinished
  const showStandaloneNew = !railsMode && pairComplete

  return (
    <div className="dwth-app">
      <header className="dwth-header">
        <h1 className="dwth-title">{labels.title || 'Draw With Two Hands'}</h1>
        <p className="dwth-subtitle">
          {labels.subtitle ||
            'Trace both shapes at the same time. Lifting a finger before both shapes are finished resets the round.'}
        </p>
        <p className="dwth-status" data-testid="dwth-status">{statusText}</p>
        {railsMode && (
          <p className="dwth-pair-counter" data-testid="dwth-pair-counter">
            {pairCounterLabel(counter, requiredPairs)}
          </p>
        )}
      </header>

      <div className="dwth-board">
        {SIDES.map((side) => (
          <section key={side} className={`dwth-side dwth-side--${side}`}>
            {!railsMode && (
              <ShapeSelector
                side={side}
                selected={shapes[side]}
                onSelect={handleSelectShape}
              />
            )}
            <ShapeCanvas
              side={side}
              shapePath={paths[side]}
              clearedCount={sides[side].cleared}
              userPath={sides[side].userPath}
              isActive={sides[side].isActive}
              bothActive={bothActive}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
            <p
              className="dwth-progress"
              data-testid={`dwth-progress-${side}`}
            >
              {sides[side].cleared}/{paths[side].length}
            </p>
          </section>
        ))}
      </div>

      {(showContinue || showStandaloneNew) && (
        <footer className="dwth-footer">
          {showContinue && (
            <button
              type="button"
              className="dwth-continue"
              onClick={handleContinue}
              data-testid="dwth-continue"
            >
              {continueLabel}
            </button>
          )}
          {showStandaloneNew && (
            <button
              type="button"
              className="dwth-new"
              onClick={handleStandaloneNew}
              data-testid="dwth-new"
            >
              {newLabel}
            </button>
          )}
        </footer>
      )}

      {railsMode &&
        (railsEnv === 'test' || railsEnv === 'development') &&
        !pairComplete && (
          <div className="dwth-dev-controls">
            <button
              type="button"
              className="dwth-fake-pair-button"
              data-testid="dwth-fake-complete-pair"
              onClick={fakeCompletePair}
            >
              [Fake Complete Pair]
            </button>
          </div>
        )}
    </div>
  )
}
